﻿import { BrowserAuthOptions } from "@azure/msal-browser";
import { createContext, FunctionComponent, PropsWithChildren, useContext, useEffect, useState } from "react";
import { PageLoading } from "../../components";

export interface BaseEnvConfig {
    apiUrl: string;
    authScopes: string[];
    aiConnectionString: string;
    aiCloudRole: string;
    aiCloudRoleInstance: string;
    env: "local" | "dev" | "qa" | "stage" | "prod";
}

export interface IEnvConfig extends BaseEnvConfig {
    authClientId: string;
    authAuthority: string;
}

export interface IEnvironmentContext extends BaseEnvConfig {
    auth: BrowserAuthOptions;
}

const defaultState: IEnvironmentContext = {
    apiUrl: "",
    auth: {
        clientId: "",
        authority: undefined,
        redirectUri: undefined,
    },
    authScopes: [],
    aiConnectionString: "",
    aiCloudRole: "",
    aiCloudRoleInstance: "",
    env: "prod",
};

export const getEnvConfig = (pathToEnvJson?: string) =>
    fetch(pathToEnvJson || "/env.json").then(response => {
        if (!response.ok) {
            throw new Error(response.statusText);
        }
        return response.json() as Promise<IEnvConfig>;
    });

const EnvironmentContext = createContext<IEnvironmentContext>(defaultState);

export type EnvironmentContextProviderProps = PropsWithChildren<{
    envJsonUrl?: string;
}>;
export const EnvironmentContextProvider: FunctionComponent<EnvironmentContextProviderProps> = ({ envJsonUrl, children }) => {
    const [envConfig, setEnvConfig] = useState<IEnvConfig | undefined>(undefined);

    // Always clear any previously cached config and re-fetch fresh on initial page load,
    // rather than trusting a stale value from a prior session.
    useEffect(() => {
        sessionStorage.removeItem("envConfig");

        getEnvConfig(envJsonUrl).then(response => {
            setEnvConfig(response);
            sessionStorage.setItem("envConfig", JSON.stringify(response));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const auth: BrowserAuthOptions = {
            clientId: envConfig?.authClientId ?? "",
            authority: envConfig?.authAuthority,
            redirectUri: window.location.origin,
        },
        authScopes = envConfig?.authScopes ?? [],
        aiConnectionString = envConfig?.aiConnectionString ?? "",
        aiCloudRole = envConfig?.aiCloudRole ?? "",
        aiCloudRoleInstance = envConfig?.aiCloudRoleInstance ?? "";

    const contextValue: IEnvironmentContext = {
        apiUrl: envConfig?.apiUrl ?? "",
        auth,
        authScopes,
        aiConnectionString,
        aiCloudRole,
        aiCloudRoleInstance,
        env: envConfig?.env ?? "prod",
    };

    return (
        <EnvironmentContext.Provider value={contextValue}>
            {envConfig ? children : <PageLoading text={"Loading Environment Context..."} />}
        </EnvironmentContext.Provider>
    );
};

export const useEnvironmentContext = () => useContext(EnvironmentContext);
